import Navbar from './components/Navbar';
import Hero from './components/Hero';
import AlertaContratacaoSeguro from './components/AlertaContratacaoSeguro';
import Stats from './components/Stats';
import Problem from './components/Problem';
import RiscosSemAssessoria from './components/RiscosSemAssessoria';
import BadInsuranceSection from './components/BadInsuranceSection';
import PerdaFinanceiraSeguro from './components/PerdaFinanceiraSeguro';
import FalsaProtecaoEmpresarial from './components/FalsaProtecaoEmpresarial';
import ImpactoIBXBrasil from './components/ImpactoIBXBrasil';
import InsuranceProfessionalizationSection from './components/InsuranceProfessionalizationSection';
import SolucaoIBXBrasil from './components/SolucaoIBXBrasil';
import CustoSemAssessoriaIBX from './components/CustoSemAssessoriaIBX';
import PatrimonioMalProtegido from './components/PatrimonioMalProtegido';
import PagandoCaroSemPerceber from './components/PagandoCaroSemPerceber';
import MetodoContratacaoAtiva from './components/MetodoContratacaoAtiva';
import DiferenciaisMetodo from './components/DiferenciaisMetodo';
import BeneficiosMetodo from './components/BeneficiosMetodo';
import AutorMetodo from './components/AutorMetodo';
import MetodoPagandoMenos from './components/MetodoPagandoMenos';
import SeguroIncendioReducaoCustosIBX from './components/SeguroIncendioReducaoCustosIBX';
import DiferencialSeguroIncendioIBX from './components/DiferencialSeguroIncendioIBX';
import ProcessoSeguroIncendioIBX from './components/ProcessoSeguroIncendioIBX';
import ReavaliacaoSeguroAutoIBX from './components/ReavaliacaoSeguroAutoIBX';
import FechamentoSeguroIncendioIBX from './components/FechamentoSeguroIncendioIBX';
import NiveisSuporte from './components/NiveisSuporte';
import Solution from './components/Solution';
import Features from './components/Features';
import Services from './components/Services';
import Testimonials from './components/Testimonials';
import About from './components/About';
import CTA from './components/CTA';
import FAQ from './components/FAQ';
import Blog from './components/Blog';
import Footer from './components/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import SEOHead from '../../components/feature/SEOHead';
import {
  useSectionColors,
  SectionColors,
  SectionBackgroundConfig,
} from '../../hooks/useSectionColors';
import ChatAssistant from '../../components/feature/ChatAssistant';

// Wrapper reativo que aplica cor de fundo customizada sem afetar o layout interno da seção
function hexToRgb(hex: string) {
  const clean = hex.replace('#', '').trim();

  if (clean.length === 3) {
    return {
      r: parseInt(clean[0] + clean[0], 16),
      g: parseInt(clean[1] + clean[1], 16),
      b: parseInt(clean[2] + clean[2], 16),
    };
  }

  if (clean.length === 6) {
    return {
      r: parseInt(clean.slice(0, 2), 16),
      g: parseInt(clean.slice(2, 4), 16),
      b: parseInt(clean.slice(4, 6), 16),
    };
  }

  return null;
}

function hexToRgba(hex: string, opacity: number) {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(15,23,42,${opacity})`;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
}

function getSectionBackgroundStyle(config?: SectionBackgroundConfig): React.CSSProperties {
  if (!config || config.mode === 'default') {
    return {};
  }

  if (config.mode === 'solid') {
    return {
      backgroundColor: config.solidColor || '#ffffff',
    };
  }

  if (config.mode === 'overlay') {
    return {
      background: hexToRgba(
        config.overlayColor || '#0f172a',
        config.overlayOpacity ?? 0.2
      ),
    };
  }

  if (config.mode === 'gradient') {
    return {
      background: `linear-gradient(${
        config.gradientDirection || 'to right'
      }, ${
        hexToRgba(config.gradientFrom || '#ffffff', config.gradientOpacity ?? 1)
      }, ${
        hexToRgba(config.gradientTo || '#f8fafc', config.gradientOpacity ?? 1)
      })`,
    };
  }

  return {};
}

function SectionBg({
  id,
  colors,
  children,
}: {
  id: string;
  colors: SectionColors;
  children: React.ReactNode;
}) {
  const config = colors[id];

  // 🚨 NOVO: controle de visibilidade
  if (config?.isActive === false) {
    return null;
  }

  const style = getSectionBackgroundStyle(config);

  if (!config || config.mode === 'default') {
    return <>{children}</>;
  }

  return (
    <div className="override-section-bg" style={style}>
      {children}
    </div>
  );
}

export default function HomePage() {
  // Hook reativo: atualiza em tempo real quando as cores mudam (mesma aba ou outra aba)
  const [colors] = useSectionColors();

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "InsuranceAgency",
    "name": "IBX Brasil",
    "description": "Consultoria especializada em seguros empresariais com foco em proteção patrimonial e gestão de riscos corporativos",
    "url": "https://ibxbrasil.com.br",
    "logo": "https://ibxbrasil.com.br/logo.png",
    "image": "https://readdy.ai/api/search-image?query=modern%20insurance%20consultancy%20office%20with%20professional%20team%20analyzing%20corporate%20insurance%20contracts%20and%20risk%20management%20documents%20in%20bright%20contemporary%20workspace&width=1200&height=630&seq=ibx-og-home&orientation=landscape",
    "telephone": "+55-11-99999-9999",
    "email": "contato@ibxbrasil.com.br",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Av. Paulista, 1000",
      "addressLocality": "São Paulo",
      "addressRegion": "SP",
      "postalCode": "01310-100",
      "addressCountry": "BR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "-23.5505",
      "longitude": "-46.6333"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      }
    ],
    "priceRange": "$$",
    "areaServed": {
      "@type": "Country",
      "name": "Brasil"
    },
    "serviceType": [
      "Consultoria em Seguros Empresariais",
      "Gestão de Contratos de Seguros",
      "Recuperação de Sinistros",
      "Análise de Riscos Corporativos"
    ],
    "sameAs": [
      "https://www.linkedin.com/company/ibxbrasil",
      "https://www.instagram.com/ibxbrasil"
    ]
  };

  return (
    <>
      <SEOHead
        title="IBX Brasil - Consultoria Especializada em Seguros Empresariais"
        description="Proteja seu patrimônio empresarial com consultoria especializada em seguros. Análise técnica, gestão de contratos e recuperação de sinistros para sua empresa."
        canonical="https://ibxbrasil.com.br/"
        ogImage="https://readdy.ai/api/search-image?query=modern%20insurance%20consultancy%20office%20with%20professional%20team%20analyzing%20corporate%20insurance%20contracts%20and%20risk%20management%20documents%20in%20bright%20contemporary%20workspace&width=1200&height=630&seq=ibx-og-home&orientation=landscape"
        schema={localBusinessSchema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        <Hero />
         <SectionBg id="stats" colors={colors}><Stats /></SectionBg>
        <SectionBg id="alerta" colors={colors}><AlertaContratacaoSeguro /></SectionBg>
        <SectionBg id="problem" colors={colors}><Problem /></SectionBg>
        <SectionBg id="riscos" colors={colors}><RiscosSemAssessoria /></SectionBg>
        <SectionBg id="bad-insurance" colors={colors}><BadInsuranceSection /></SectionBg>
        <SectionBg id="perda-financeira" colors={colors}><PerdaFinanceiraSeguro /></SectionBg>
        <SectionBg id="falsa-protecao" colors={colors}><FalsaProtecaoEmpresarial /></SectionBg>
        <SectionBg id="impacto-ibx" colors={colors}><ImpactoIBXBrasil /></SectionBg>
        <SectionBg id="profissionalizacao" colors={colors}><InsuranceProfessionalizationSection /></SectionBg>
        <SectionBg id="solucao-ibx" colors={colors}><SolucaoIBXBrasil /></SectionBg>
        <SectionBg id="custo-sem-assessoria" colors={colors}><CustoSemAssessoriaIBX /></SectionBg>
        <SectionBg id="patrimonio" colors={colors}><PatrimonioMalProtegido /></SectionBg>
        <SectionBg id="pagando-caro" colors={colors}><PagandoCaroSemPerceber /></SectionBg>
        <SectionBg id="metodo" colors={colors}><MetodoContratacaoAtiva /></SectionBg>
        <SectionBg id="diferenciais" colors={colors}><DiferenciaisMetodo /></SectionBg>
        <SectionBg id="beneficios" colors={colors}><BeneficiosMetodo /></SectionBg>
        <SectionBg id="autor-metodo" colors={colors}><AutorMetodo /></SectionBg>
        <SectionBg id="niveis-suporte" colors={colors}><NiveisSuporte /></SectionBg>
        <SectionBg id="solution" colors={colors}><Solution /></SectionBg>
        <SectionBg id="features" colors={colors}><Features /></SectionBg>
        <SectionBg id="services" colors={colors}><Services /></SectionBg>
        <SectionBg id="testimonials" colors={colors}><Testimonials /></SectionBg>
        <SectionBg id="about" colors={colors}><About /></SectionBg>
        <SectionBg id="cta" colors={colors}><CTA /></SectionBg>
        <SectionBg id="faq" colors={colors}><FAQ /></SectionBg>
        <SectionBg id="blog" colors={colors}><Blog /></SectionBg>
        <SectionBg id="FechamentoSeguroIncendioIBX" colors={colors}><FechamentoSeguroIncendioIBX /></SectionBg>
        <SectionBg id="ReavaliacaoSeguroAutoIBX" colors={colors}><ReavaliacaoSeguroAutoIBX /></SectionBg>
        <SectionBg id="footer" colors={colors}><Footer /></SectionBg>
        <ChatAssistant />
        <WhatsAppButton />
      </div>
    </>
  );
}